<template>
  <div>
    <div class="page-section pb-0">
      <div :class="containerClass">
        <div class="analytics-topbar">
        <b-btn
          variant="primary"
            class="analytics-topbar__leaderboard-btn d-inline-flex align-items-center"
          @click.prevent="goToLeaderboard"
        >
          <span class="material-icons icon-16pt mr-1">emoji_events</span>
          Course Leaderboard
        </b-btn>
      </div>
    </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <!-- ============== Global Filter Bar ============== -->
        <div class="card analytics-card global-filter-card mb-4">
          <div class="card-body">
            <div class="filter-row">
              <div class="filter-item filter-item--lg">
                <label>Date Range</label>
                <date-picker
                  v-model="filters.dateRange"
                  format="MMM DD, YYYY"
                  valueType="YYYY-MM-DD"
                  range
                  class="w-100"
                  placeholder="Start - End"
                />
              </div>
              <div class="filter-item filter-item--lg">
                <label>Course</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  v-model="filters.course"
                  :reduce="o => o.id"
                  :options="allCourses"
                  placeholder="All Courses"
                  :clearable="true"
                />
              </div>
              <div class="filter-item">
                <label>Program</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  v-model="filters.program"
                  :reduce="o => o.id"
                  :options="allPrograms"
                  placeholder="All Programs"
                  :clearable="true"
                />
              </div>
              <div class="filter-item">
                <label>Cohort/Tag</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  v-model="filters.tag"
                  :reduce="o => o.id"
                  :options="allTags"
                  placeholder="All Tags"
                  :clearable="true"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- ============== Enrollment Overview (KPIs) ============== -->
        <div class="card mb-4 analytics-card">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Enrollment Overview</h5>
                <p class="section-sub">Snapshot of your school’s LMS enrollments and performance.</p>
              </div>
            </div>

            <div v-if="isFunnelLoading" class="text-center my-4">
              <b-spinner class="align-middle mr-2" /><strong>Loading metrics...</strong>
            </div>
            <div v-else class="kpi-grid">
              <div v-for="card in kpiCards" :key="card.label" class="kpi-card">
                <div class="kpi-card__header">
                  <div :class="['kpi-card__icon', card.iconClass]">
                    <i class="material-icons">{{ card.icon }}</i>
                  </div>
                  <span class="kpi-card__label">{{ card.label }}</span>
                </div>
                <div class="kpi-card__value">
                  {{ card.value }}<span v-if="card.suffix" class="kpi-card__suffix">{{ card.suffix }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============== Enrollments Over Time ============== -->
        <div class="card mb-4 analytics-card">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Enrollments Over Time</h5>
                <p class="section-sub">
                  Time-series of enrollments. Time Frame and Status below only shape this chart.
                </p>
              </div>
              <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                <v-select
                  class="form-control v-select-custom chart-control"
                  label="label"
                  v-model="chart.timeFrame"
                  :reduce="o => o.value"
                  :options="timeFrameOptions"
                  :clearable="false"
                />
                <v-select
                  class="form-control v-select-custom chart-control"
                  label="label"
                  v-model="chart.status"
                  :reduce="o => o.value"
                  :options="enrollStatusOptions"
                  placeholder="All Statuses"
                  :clearable="true"
                />
                <!-- <b-btn
                  variant="primary"
                  size="sm"
                  :disabled="isOverTimeExportLoading"
                  @click.prevent="exportEnrollmentsOverTimeRoster"
                >
                  Export CSV
                  <i v-if="isOverTimeExportLoading" class="fas fa-circle-notch fa-spin ml-1"></i>
                  <span v-else class="material-icons icon-16pt ml-1">file_download</span>
                </b-btn> -->
              </div>
            </div>

            <div class="chart-container mt-3">
              <div v-if="isChartLoading" class="text-center py-5">
                <b-spinner class="align-middle mr-2" /><strong>Loading chart...</strong>
              </div>
              <canvas v-show="!isChartLoading" id="lms-enrollments-graph"></canvas>
            </div>
          </div>
        </div>

        <!-- ============== General Roster Report ============== -->
        <div class="card mb-4 analytics-card">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Generate Roster Report</h5>
                <p class="section-sub">
                  Uses the filters above. If a Course is selected only that course is included; otherwise all courses in scope.
                </p>
              </div>
            </div>

            <div class="row align-items-end report-top-row">
              <div class="col-md-4">
                <label class="fw600">Enrollment Status</label>
                <v-select
                  class="form-control v-select-custom"
                  label="label"
                  v-model="report.enrollStatus"
                  :reduce="o => o.value"
                  :options="enrollStatusOptions"
                  placeholder="All Statuses"
                  :clearable="true"
                />
              </div>
              <div class="col-md-8 d-flex justify-content-md-end mt-3 mt-md-0">
                <b-btn
                  variant="primary"
                  class="d-inline-flex align-items-center"
                  :disabled="isReportLoading"
                  @click.prevent="generateRosterReport"
                >
                  <i v-if="isReportLoading" class="fas fa-circle-notch fa-spin mr-1"></i>
                  <span v-else class="material-icons icon-16pt mr-1">description</span>
                  Generate Roster Report
                </b-btn>
              </div>
            </div>

            <label class="fw600 mt-4 mb-2 d-block">Include columns</label>
            <div class="row">
              <div
                v-for="opt in includeOptions"
                :key="opt.key"
                class="col-md-3 col-sm-6"
              >
                <b-form-checkbox v-model="report.include[opt.key]">
                  {{ opt.label }}
                </b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DatePicker from 'vue2-datepicker';
import vSelect from 'vue-select';
import moment from 'moment';
import { Chart } from 'chart.js';
import Page from '@/components/Page.vue';
import { formatToAPIDate } from '../../../common/utils';

const defaultRange = () => [
  formatToAPIDate(moment().startOf('month')),
  formatToAPIDate(moment().endOf('month')),
];

export default {
  components: { DatePicker, vSelect },
  extends: Page,
  data() {
    return {
      title: 'School LMS Analytics',

      // Lookup data
      allCourses: [],
      allPrograms: [],
      allTags: [],

      // Single global filter (applies to KPIs, chart, general report)
      filters: {
        dateRange: defaultRange(),
        course: null,
        program: null,
        tag: null,
      },

      // Chart-only shape controls (not scope filters)
      chart: {
        timeFrame: 'monthly',
        status: null,
      },

      timeFrameOptions: [
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
      ],
      enrollStatusOptions: [
        { value: 'enrolled', label: 'Enrolled' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'dropped', label: 'Dropped' },
      ],
      includeOptions: [
        { key: 'progress_breakdown', label: 'Progress Breakdown' },
        { key: 'quiz_results', label: 'Quiz Results' },
        { key: 'assignment_grades', label: 'Assignment Grades' },
        { key: 'time_spent', label: 'Time Spent' },
      ],
      report: {
        enrollStatus: null,
        include: {
          progress_breakdown: true,
          quiz_results: true,
          assignment_grades: true,
          time_spent: true,
        },
      },

      // Card state
      funnel: {},
      isFunnelLoading: false,

      chartInstance: null,
      isChartLoading: false,
      isOverTimeExportLoading: false,

      isReportLoading: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getAuthToken']),
    primaryColor() {
      return (this.getLoggedInUser && this.getLoggedInUser.linked_entity
        && this.getLoggedInUser.linked_entity.primary_color) || '#667eea';
    },
    kpiCards() {
      const t = this.funnel.totals || {};
      return [
        { label: 'Total Enrolled', icon: 'people', iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(t.enrolled) },
        { label: 'In Progress', icon: 'hourglass_empty', iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(t.in_progress) },
        { label: 'Completed', icon: 'check_circle', iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(t.completed) },
        { label: 'Dropped', icon: 'cancel', iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(t.dropped) },
        { label: 'Avg Progress', icon: 'timeline', iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(t.avg_progress_percent), suffix: '%' },
        { label: 'Avg Grade', icon: 'grade', iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(t.avg_grade), suffix: '%' },
        { label: 'Pass Rate', icon: 'emoji_events', iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(t.pass_rate), suffix: '%' },
        { label: 'Avg Time to Complete', icon: 'schedule', iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(t.avg_time_to_complete_days), suffix: ' days' },
      ];
    },
    baseParams() {
      const range = this.filters.dateRange || [];
      return {
        course_id: this.filters.course || null,
        program_id: this.filters.program || null,
        tag_id: this.filters.tag || null,
        date_from: range[0] || null,
        date_to: range[1] || null,
      };
    },
  },
  methods: {
    ...mapActions('lmsAnalytics', [
      'getEnrollmentsGraph',
      'getCompletionFunnel',
      'exportEnrollmentsOverTime',
      'createCourseRosterReport',
    ]),
    ...mapActions('courses', ['getLmsCourses']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('attendance', ['getTags']),
    ...mapActions('fileDownload', ['downloadFile']),

    formatPct(v) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      return Number.isNaN(n) ? '0' : n.toFixed(1);
    },
    formatInt(v) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      return Number.isNaN(n) ? '0' : n.toLocaleString();
    },
    unwrapListEnvelope(res) {
      const payload = res && res.data ? res.data : res;
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.results)) return payload.results;
      if (payload.data) {
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.data.results)) return payload.data.results;
      }
      return [];
    },
    validateRange(maxMonths = 12) {
      const r = this.filters.dateRange;
      if (!r || !r[0] || !r[1]) return true;
      const diff = moment(r[1]).diff(moment(r[0]), 'months', true);
      if (diff > maxMonths) {
        this.makeToast({ variant: 'danger', msg: `Date range must be ${maxMonths} months or less.` });
        return false;
      }
      return true;
    },

    // -------- Navigation --------
    goToLeaderboard() {
      const r = this.filters.dateRange || [];
      this.$router.push({
        name: 'school-lms-leaderboard',
        query: {
          date_from: r[0] || undefined,
          date_to: r[1] || undefined,
          course_id: this.filters.course || undefined,
          program_id: this.filters.program || undefined,
          tag_id: this.filters.tag || undefined,
        },
      });
    },

    // -------- Lookups --------
    async fetchLookups() {
      try {
        const [coursesRes, progRes, tagsRes] = await Promise.all([
          this.getLmsCourses({ limit: 200 }),
          this.getAllPrograms(),
          this.getTags({ limit: 200 }).catch(() => null),
        ]);
        const courseList = this.unwrapListEnvelope(coursesRes);
        this.allCourses = (Array.isArray(courseList) ? courseList : []).map(c => ({
          id: c.id, title: c.title,
        }));
        this.allPrograms = (progRes && progRes.data) || [];
        const tagList = this.unwrapListEnvelope(tagsRes);
        this.allTags = (Array.isArray(tagList) ? tagList : []).map(t => ({ id: t.id, title: t.title || t.name }));
      } catch (e) { /* silent */ }
    },

    // -------- Funnel --------
    async fetchFunnel() {
      if (!this.validateRange(24)) return;
      this.isFunnelLoading = true;
      try {
        const res = await this.getCompletionFunnel(this.baseParams);
        this.funnel = res.data || res || {};
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load overview metrics.' });
      }
      this.isFunnelLoading = false;
    },

    // -------- Chart --------
    async fetchAndUpdateChart() {
      if (!this.validateRange(12)) return;
      this.isChartLoading = true;
      try {
        const res = await this.getEnrollmentsGraph({
          ...this.baseParams,
          time_frame: this.chart.timeFrame,
          enroll_status: this.chart.status || null,
        });
        const payload = res.data || res || {};
        this.$nextTick(() => {
          this.renderChart(payload);
          this.isChartLoading = false;
        });
      } catch (e) {
        this.isChartLoading = false;
        this.makeToast({ variant: 'danger', msg: 'Failed to load enrollments chart.' });
      }
    },
    renderChart(payload) {
      const ctx = document.getElementById('lms-enrollments-graph');
      if (!ctx) return;
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
      const statusOpt = this.enrollStatusOptions.find(o => o.value === this.chart.status);
      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: payload.labels || [],
          datasets: [{
            label: statusOpt ? statusOpt.label : 'All Enrollments',
            data: payload.datasets || [],
            backgroundColor: this.primaryColor,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'top' } },
          scales: {
            yAxes: [{ ticks: { beginAtZero: true } }],
            xAxes: [{
              scaleLabel: {
                display: !!(payload.date_from && payload.date_to),
                labelString: payload.date_from && payload.date_to
                  ? `(${payload.date_from} - ${payload.date_to})` : '',
              },
            }],
          },
        },
      });
    },

    // -------- Exports --------
    async runDownload(url) {
      try {
        await this.downloadFile({ fileUrl: url });
        this.makeToast({ variant: 'success', msg: 'File downloaded.' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to download file.' });
      }
    },
    unwrapUrl(res) {
      return (res && res.data && res.data.url) || (res && res.url) || null;
    },
    async exportEnrollmentsOverTimeRoster() {
      if (!this.validateRange(12)) return;
      this.isOverTimeExportLoading = true;
      try {
        const res = await this.exportEnrollmentsOverTime({
          ...this.baseParams,
          time_frame: this.chart.timeFrame,
          enroll_status: this.chart.status || null,
        });
        const url = this.unwrapUrl(res);
        if (url) await this.runDownload(url);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to export enrollments CSV.' });
      }
      this.isOverTimeExportLoading = false;
    },

    // -------- General Roster Report (POST course-roster-report/) --------
    async generateRosterReport() {
      if (!this.validateRange(12)) return;
      const body = {
        course_ids: this.filters.course ? [this.filters.course] : [],
        program_id: this.filters.program || null,
        tag_id: this.filters.tag || null,
        date_from: (this.filters.dateRange && this.filters.dateRange[0]) || null,
        date_to: (this.filters.dateRange && this.filters.dateRange[1]) || null,
        enroll_status: this.report.enrollStatus || null,
        include: { ...this.report.include },
      };
      this.isReportLoading = true;
      try {
        const res = await this.createCourseRosterReport(body);
        const url = this.unwrapUrl(res);
        if (url) {
          await this.runDownload(url);
        } else {
          this.makeToast({ variant: 'danger', msg: 'Report generated but no URL was returned.' });
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to generate roster report.' });
      }
      this.isReportLoading = false;
    },

    refreshMainCards() {
      this.fetchFunnel();
      this.fetchAndUpdateChart();
    },
  },
  watch: {
    'filters.dateRange': 'refreshMainCards',
    'filters.course': 'refreshMainCards',
    'filters.program': 'refreshMainCards',
    'filters.tag': 'refreshMainCards',
    'chart.timeFrame': 'fetchAndUpdateChart',
    'chart.status': 'fetchAndUpdateChart',
  },
  async mounted() {
    await this.fetchLookups();
    this.refreshMainCards();
  },
  beforeDestroy() {
    if (this.chartInstance) {
      try { this.chartInstance.destroy(); } catch (e) { /* noop */ }
    }
  },
};
</script>

<style scoped lang="scss">
.fw600 { font-weight: 600; }

.analytics-topbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.analytics-topbar__title {
  margin-bottom: 0 !important;
}

.analytics-topbar__leaderboard-btn {
  margin-top: 6px;
}

@media (max-width: 767.98px) {
  .analytics-topbar {
    align-items: stretch;
  }

  .analytics-topbar__leaderboard-btn {
    justify-content: center;
    width: 100%;
    margin-top: 0;
  }
}

.analytics-card {
  border: 1px solid #e7ecf5;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
  background: #fff;
}

.global-filter-card { position: sticky; top: 8px; z-index: 5; }

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 16px;
  flex-wrap: wrap;
}
.section-title {
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px;
  font-size: 1.05rem;
  letter-spacing: 0.01em;
}
.section-sub {
  margin: 0;
  color: #64748b;
  font-size: 0.85rem;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.filter-item {
  flex: 1 1 180px;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0;
  }
}
.filter-item--lg { flex: 1 1 240px; min-width: 220px; }

.chart-control { width: 160px; min-width: 140px; }

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
}
.kpi-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 22px rgba(15,23,42,0.08); }
}
.kpi-card__header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.kpi-card__icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  i { font-size: 18px; color: #fff; }
  &--blue   { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
  &--green  { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
  &--red    { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
  &--purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
  &--orange { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
  &--grey   { background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%); }
}
.kpi-card__label { font-size: 0.72rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.kpi-card__value { font-size: 1.6rem; font-weight: 700; color: #1f2937; line-height: 1; display: flex; align-items: baseline; gap: 4px; }
.kpi-card__suffix { font-size: 0.95rem; color: #6b7280; }

.chart-container {
  position: relative;
  height: 360px;
}

.report-top-row .btn {
  white-space: nowrap;
}
</style>
